/**
 * Every country the product offers, NAMED IN THE READER'S LANGUAGE and sorted
 * the way the reader's language sorts. (F34)
 *
 * `COUNTRIES` in `@tonnie/foundation` carries an ENGLISH `name`, and every
 * country picker in the three portals rendered it — a pt-PT citizen chose
 * "Brazil" from a list sorted as English sorts it. This is the list a picker
 * renders: the same codes, flags and dialling codes, with `name` from
 * `useCountryName` (the typed `common.countries.<ALPHA-2>` keys) and an order
 * from `Intl.Collator` in the app locale. The apps' lint config bans importing
 * the English lookups (`COUNTRIES`, `getCountryByCode`, `getCountryName`, …).
 */
import { useMemo } from 'react';
import { COUNTRIES } from '@tonnie/foundation';

import { useCountryName } from './useCountryName';
import { useTranslation } from './useTranslation';

export interface LocalizedCountry {
  /** ISO 3166-1 alpha-2. */
  code: string;
  /** The country's name in the reader's language. */
  name: string;
  /** Flag emoji — React Native only; web renders `<CountryFlag>`. */
  emoji: string;
  /** International dialling code, e.g. `+351`. */
  phoneCode: string;
}

export interface LocalizedCountries {
  /** Every offered country, sorted by its localized name. */
  countries: readonly LocalizedCountry[];
  /** One country by code (case-insensitive), or `undefined` if not offered. */
  getCountry: (code: string | null | undefined) => LocalizedCountry | undefined;
}

export function useCountries(): LocalizedCountries {
  const { i18n } = useTranslation();
  const countryName = useCountryName();
  const language = i18n.language;

  return useMemo(() => {
    const collator = new Intl.Collator(language);
    const countries = COUNTRIES.map((c) => ({
      code: c.code,
      name: countryName(c.code),
      emoji: c.emoji,
      phoneCode: c.phoneCode,
    })).sort((a, b) => collator.compare(a.name, b.name));
    const byCode = new Map(countries.map((c) => [c.code, c]));
    return {
      countries,
      getCountry: (code) => (code ? byCode.get(code.toUpperCase()) : undefined),
    };
  }, [countryName, language]);
}
